import React from "react";
import { useIntl } from "react-intl";
import { useFormContext } from "react-hook-form";
import { Dropdown } from "../../../components/controls/Dropdown";
import { svgIcon } from "../../../utils/svgProvider";
import { useDispatch, useSelector } from "../../../hooks";
import {
	handleFieldValueChange,
	isLookupMissingSubunitData,
	resetLookupData,
	toggleCompanyDataEditMode,
	toggleSubunitSearch,
	triggerAlert,
	validateCompany,
	validateSubunit,
	onAlertClose,
} from "../slices/register.slice";
import {
	searchCompanyData,
	searchCompanyDetails,
	searchCompanySubUnits,
} from "../slices/register.actions";
import { PayloadAction } from "@reduxjs/toolkit";
import { useDebouncedCallback } from "use-debounce";
import { COMPANY_TYPES } from "./Company.utils";
import Alert from "../../../components/common/Alert";
import { Text } from "../../../components/controls/Text";
import { trackEvent } from "../../../utils/tracking";
import { TRACKING_EVENTS } from "../../../utils/trackingDefinitions";
import { TYPE_DEBOUNCE } from "../../settings/common.utils";

interface CompanySearchProps {
	isFormOpen: boolean;
	onChange: (event: any) => void;
}

type LookupFields =
	| "organisationNumber"
	| "businessName"
	| "companyAddress"
	| "postcode"
	| "city";

export const CompanySearch = (props: CompanySearchProps) => {
	const dispatch = useDispatch();
	const { formatMessage } = useIntl();

	const businessName = useSelector(
		(state) => state.registration.b2b.form.businessName,
	);

	const organisationNumber = useSelector(
		(state) => state.registration.b2b.form.organisationNumber,
	);

	const subunitOrganisationNumber = useSelector(
		(state) => state.registration.b2b.form.subunitOrganisationNumber,
	);

	const canonicalId = useSelector(
		(state) => state.registration.b2b.form.canonicalId,
	);

	const companyData = useSelector(
		(state) => state.registration.b2b.form.companyData,
	);

	const subunitsCompanyData = useSelector(
		(state) => state.registration.b2b.form.subunitsCompanyData,
	);

	const isFetchingCompanyData = useSelector(
		(state) => state.registration.b2b.form.isFetchingCompanyData,
	);
	const isFetchingSubunits = useSelector(
		(state) => state.registration.b2b.form.isFetchingSubunits,
	);

	const hasNoResults = useSelector(
		(state) => state.registration.b2b.hasNoResults,
	);

	const country = useSelector((state) => state.settings.country);

	const useGovernmentSearch = useSelector(
		(state) => state.settings.features.useGovernmentSearch,
	);

	const missingSubunitData = useSelector(
		(state) => state.registration.b2b.missingSubunitData,
	);

	const organisationNumberValidated = useSelector(
		(state) => state.registration.b2b.organisationNumberValidated,
	);

	const businessNameValidated = useSelector(
		(state) => state.registration.b2b.businessNameValidated,
	);

	const subunitOrganisationNumberValidated = useSelector(
		(state) => state.registration.b2b.subunitOrganisationNumberValidated,
	);

	const isSubunitSearchActive = useSelector(
		(state) => state.registration.b2b.isSubunitSearchActive,
	);

	const {
		register,
		trigger,
		setValue,
		setError,
		setFocus,
		getValues,
		resetField,
		clearErrors,
		formState: { errors },
	} = useFormContext();

	const handleCompanyLookup = useDebouncedCallback(async (value) => {
		if (value) {
			dispatch(onAlertClose([]));
			clearErrors();

			const response: PayloadAction<{
				result?: [];
				error?: any;
			}> = await dispatch(
				searchCompanyData({
					country,
					query: value,
					companyType: isSubunitSearchActive
						? COMPANY_TYPES.GOVERNMENT
						: COMPANY_TYPES.NON_GOVERNMENT,
				}),
			);

			if (response.payload?.result?.length === 0) {
				setError("organisationNumber", {
					type: "custom",
					message: "komid.register.company.name.not.exists",
				});
			}
		}
	}, TYPE_DEBOUNCE);

	const handleSubunitCompanyLookup = useDebouncedCallback((value) => {
		if (value) {
			dispatch(onAlertClose([]));
			clearErrors();

			dispatch(
				searchCompanySubUnits({
					canonicalId,
					query: value,
				}),
			);
		}
	}, TYPE_DEBOUNCE);

	const updateCompanyData = (companyData) => {
		const fields = {
			businessName: companyData.name,
			organisationNumber: companyData.national_identifier?.id,
			companyAddress: companyData.addresses?.[0]?.street_address,
			postcode: companyData.addresses?.[0]?.postal_code,
			city: companyData.addresses?.[0]?.city,
		};

		const missingCompanyData = Object.entries(fields).some(
			([field, value]) => !value,
		);

		if (missingCompanyData) {
			dispatch(
				triggerAlert([
					{
						level: "error",
						messageKey: "komid.register.lookup.data.company.validation.missing",
					},
				]),
			);
			dispatch(toggleCompanyDataEditMode(true));
		}

		Object.entries(fields).forEach(
			async ([field, value]: [LookupFields, string | null]) => {
				setValue(field, value);

				const isValid = await trigger(field);
				if (!isValid) {
					setError(field, {
						type: "custom",
						message: "komid.register.validation.required",
					});

					if (field === "businessName") {
						dispatch(validateCompany(false));
						setError("businessName", {
							type: "custom",
							message: "komid.register.validation.company.length.max",
						});
					}
				}
			},
		);

		if (companyData.companyType === COMPANY_TYPES.SOLE_TRADER) {
			dispatch(
				handleFieldValueChange({
					form: "b2b",
					field: "organisationNumber",
					value: companyData.national_identifier?.id,
				}),
			);
		}
	};

	const toggleSubunitSearchMode = () => {
		const isOpen = !isSubunitSearchActive;
		const value = getValues("organisationNumber");

		dispatch(onAlertClose([]));
		dispatch(resetLookupData());
		dispatch(toggleSubunitSearch(isOpen));

		if (value && isOpen) {
			handleCompanyLookup(value);
		} else {
			setFocus("organisationNumber");
			resetField("organisationNumber");
			resetField("subunitOrganisationNumber");
		}
	};

	const onCompanySearch = async ({ target: { id, value }, type }) => {
		if (type === "select") {
			const { lookupId, companyType } = companyData.find(
				(company) => company.value === value,
			);

			const response: PayloadAction<{
				lookupId?: string;
				result?: {
					canonical_id?: string;
					name: string;
					national_identifier: {
						id: string;
					};
					addresses: [
						{ street_address: string; postal_code: string; city: string },
					];
				};
				error?: any;
			}> = await dispatch(searchCompanyDetails({ lookupId }));

			updateCompanyData({ ...response.payload.result, companyType });

			if (isSubunitSearchActive) {
				dispatch(validateSubunit(false));
				setValue("subunitOrganisationNumber", "");

				dispatch(
					handleFieldValueChange({
						form: "b2b",
						field: "subunitsCompanyData",
						value: [],
					}),
				);

				const subunits = await dispatch(
					searchCompanySubUnits({
						canonicalId: response.payload.result.canonical_id,
						query: "",
					}),
				);

				dispatch(
					isLookupMissingSubunitData(
						(subunits.payload as { result?: any[] })?.result?.length === 0,
					),
				);
			}
		} else {
			handleCompanyLookup(value);
		}
	};

	const onCompanySubunitSelect = ({ target: { id, value }, type }) => {
		if (type === "select") {
			const subunit = subunitsCompanyData.find(
				(subunit) => subunit.value === value,
			);

			if (subunit) {
				const { key, value } = subunit;
				const fields = {
					subunitOrganisationNumber: value,
					subunitName: key,
				};

				Object.entries(fields).forEach(
					async ([field, value]: [
						"subunitOrganisationNumber" | "subunitName",
						string | null,
					]) => {
						setValue(field, value);
						props.onChange({ target: { id: field, value }, isTrusted: true });

						const isValid = await trigger(field);
						if (!isValid) {
							setError(field, {
								type: "custom",
								message: "komid.register.validation.required",
							});
						}
					},
				);

				dispatch(validateSubunit(true));
				trackEvent({
					event: TRACKING_EVENTS.REGISTRATION.EVENT.USER_INTERACTION,
					target: TRACKING_EVENTS.REGISTRATION.APP,
					value: "select_subunit_organization_number",
				});
			}
		} else {
			handleSubunitCompanyLookup(value);
		}
	};

	return (
		<>
			{props.isFormOpen && !businessNameValidated ? (
				<Text
					id="businessName"
					className="form-input"
					ref={
						register("businessName", {
							value: businessName,
						})?.ref
					}
					error={
						typeof errors.businessName?.message === "string"
							? errors.businessName.message
							: undefined
					}
					value={businessName}
					onChange={(event) => {
						props.onChange(event);
					}}
					label={formatMessage({
						id: "komid.register.company.name",
					})}
				/>
			) : (
				<Dropdown
					id="organisationNumber"
					className="form-input"
					label={formatMessage({
						id: isSubunitSearchActive
							? "komid.register.company.name.or.organization"
							: "komid.register.main.unit.company.name",
					})}
					placeholder={formatMessage({
						id: isSubunitSearchActive
							? "komid.register.main.unit.company.name.placeholder"
							: "komid.register.company.name.placeholder",
					})}
					ref={
						register("organisationNumber", {
							value: organisationNumber,
						})?.ref
					}
					error={
						typeof errors.organisationNumber?.message === "string"
							? errors.organisationNumber.message
							: undefined
					}
					items={companyData}
					withSearch
					autoFocus
					disabled={props.isFormOpen}
					isFetching={isFetchingCompanyData}
					onChange={(event) => {
						props.onChange(event);
						onCompanySearch(event);
					}}
					selectedItem={(item) => {
						return item.companyType === COMPANY_TYPES.SOLE_TRADER
							? item.key
							: `${item.key}, ${formatMessage({
									id: "komid.register.business.organization.number.short",
								})} ${item.value}`;
					}}
					renderItem={(item) => (
						<div className="option-wrapper">
							<span>
								<p className="title">{item.key}</p>
								{item.companyType !== COMPANY_TYPES.SOLE_TRADER && (
									<p className="subtitle">
										{formatMessage({
											id: "komid.register.business.organization.number.short",
										})}{" "}
										{item.value}
									</p>
								)}
							</span>
							<span>
								{item.isSelected
									? svgIcon.radioSelected
									: svgIcon.radioUnselected}
							</span>
						</div>
					)}
				/>
			)}

			{useGovernmentSearch && isSubunitSearchActive && !missingSubunitData && (
				<>
					<Dropdown
						id="subunitOrganisationNumber"
						className="form-input"
						label={
							props.isFormOpen && subunitOrganisationNumberValidated
								? formatMessage({
										id: "komid.register.subunit.company.name.selected",
									})
								: formatMessage({ id: "komid.register.subunit.company.name" })
						}
						ref={
							register("subunitOrganisationNumber", {
								value: subunitOrganisationNumber,
							})?.ref
						}
						error={
							typeof errors.subunitOrganisationNumber?.message === "string"
								? errors.subunitOrganisationNumber.message
								: undefined
						}
						disabled={props.isFormOpen || !organisationNumberValidated}
						withSearch
						autoFocus={isSubunitSearchActive && organisationNumberValidated}
						isFetching={isFetchingSubunits}
						items={subunitsCompanyData}
						onChange={(event) => {
							props.onChange(event);
							onCompanySubunitSelect(event);
						}}
						selectedItem={(item) =>
							`${item.key}, ${formatMessage({
								id: "komid.register.business.organization.number.short",
							})} ${item.value}`
						}
						renderItem={(item) => (
							<div className="option-wrapper">
								<span>
									<p className="title">{item.key}</p>
									<p className="subtitle">
										{formatMessage({
											id: "komid.register.business.organization.number.short",
										})}{" "}
										{item.value}
									</p>
								</span>
								<span>
									{item.isSelected
										? svgIcon.radioSelected
										: svgIcon.radioUnselected}
								</span>
							</div>
						)}
					/>
					{!props.isFormOpen && (
						<div className="form-notice">
							<Alert
								level="info"
								messageKey="komid.register.subunit.company.search.notice"
							/>
						</div>
					)}
				</>
			)}
			<>
				{useGovernmentSearch && (
					<>
						{!props.isFormOpen ? (
							<a
								href="#"
								className="link"
								data-testid={
									isSubunitSearchActive
										? "company.search.private.link"
										: "company.search.government.link"
								}
								onClick={toggleSubunitSearchMode}
							>
								{!isSubunitSearchActive
									? formatMessage({
											id: "komid.register.government.company.search",
										})
									: formatMessage({
											id: "komid.register.private.company.search",
										})}
							</a>
						) : (
							<div className="form-separator">
								<span>
									{formatMessage({ id: "komid.register.form.fill.details" })}
								</span>
							</div>
						)}
					</>
				)}
			</>

			{!props.isFormOpen && !isSubunitSearchActive && hasNoResults && (
				<div className="form-notice">
					<Alert
						level="warning"
						messageKey="komid.register.cant.find.your.company"
					/>
				</div>
			)}
		</>
	);
};
